/**
 * Client-side image compression for storyverse/character uploads.
 * Matches backend: max 12MB; resize max 1024px (longer side in bounds); JPEG quality ~0.7
 */

export const MAX_CHARACTER_IMAGE_BYTES = 12 * 1024 * 1024;
export const MAX_CHARACTER_IMAGE_WIDTH = 1024;
export const MAX_CHARACTER_IMAGE_HEIGHT = 1024;
export const CHARACTER_JPEG_QUALITY = 0.7;

/**
 * @param {string} label
 * @param {Blob|File} blob
 * @param {{ log?: (msg: string) => void }} [opts] — defaults to console.log
 */
export function logCharacterImageSize(
  label,
  blob,
  { log = (msg) => console.log(msg) } = {}
) {
  if (blob == null) {
    log(`[createCharacter] ${label}: (none)`);
    return;
  }
  const n = blob.size;
  const kb = (n / 1024).toFixed(1);
  const mb = (n / (1024 * 1024)).toFixed(2);
  log(`[createCharacter] ${label}: ${n} bytes (~${kb} KB, ~${mb} MB)`);
}

/**
 * @param {Blob|File} file
 * @param {number} [maxBytes=MAX_CHARACTER_IMAGE_BYTES]
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function assertUnderMaxUploadSize(
  file,
  maxBytes = MAX_CHARACTER_IMAGE_BYTES
) {
  if (!file) {
    return { ok: false, message: "Missing image data." };
  }
  if (file.size > maxBytes) {
    return {
      ok: false,
      message: `Image is still too large after compression (~${(file.size / (1024 * 1024)).toFixed(1)} MB). Max allowed is ${(maxBytes / (1024 * 1024)).toFixed(0)} MB. Try a smaller or simpler image.`,
    };
  }
  return { ok: true };
}

/**
 * Resizes to fit within maxW×maxH, draws on white, exports JPEG.
 * @param {Blob|File} file
 * @param {Object} [options]
 * @param {number} [options.maxWidth=1024]
 * @param {number} [options.maxHeight=1024]
 * @param {number} [options.quality=0.7]
 * @param {string} [options.outputFileName="image.jpg"]
 * @returns {Promise<File>}
 */
export function compressImage(file, options = {}) {
  const maxW = options.maxWidth ?? MAX_CHARACTER_IMAGE_WIDTH;
  const maxH = options.maxHeight ?? MAX_CHARACTER_IMAGE_HEIGHT;
  const quality = options.quality ?? CHARACTER_JPEG_QUALITY;
  let outputFileName = options.outputFileName ?? "image.jpg";
  if (!/\.(jpe?g)$/i.test(outputFileName)) {
    outputFileName = outputFileName.replace(/\.[^.]+$/, "") + ".jpg";
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxW) {
          height = (height * maxW) / width;
          width = maxW;
        }
      } else {
        if (height > maxH) {
          width = (width * maxH) / height;
          height = maxH;
        }
      }

      width = Math.max(1, Math.round(width));
      height = Math.max(1, Math.round(height));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("2D canvas not available"));
        return;
      }

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to export JPEG"));
            return;
          }
          resolve(
            new File([blob], outputFileName, {
              type: "image/jpeg",
              lastModified: Date.now(),
            })
          );
        },
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to decode image for compression"));
    };

    img.src = url;
  });
}
